// ╔══════════════════════════════════════════════════════════════════╗
// ║                    ADMIN ACCOUNTS PAGE                           ║
// ╚══════════════════════════════════════════════════════════════════╝

import React, { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  CreditCard,
  Search,
  Filter,
  ChevronLeft,
  ChevronRight,
  Eye,
  Edit2,
  DollarSign,
  AlertCircle,
  CheckCircle,
  XCircle,
  Loader2,
  X,
  User,
  Calendar,
  Hash,
  TrendingUp,
  TrendingDown,
  Ban,
  RefreshCw,
  Plus,
  Minus
} from 'lucide-react'
import { AccountsAdminAPI } from '../../services/adminApi'
import { useAdmin } from '../AdminApp'

export default function AccountsPage() {
  const { hasPermission } = useAdmin()
  const [accounts, setAccounts] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  // Filters
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [typeFilter, setTypeFilter] = useState('')

  // Pagination
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [total, setTotal] = useState(0)

  // Modals
  const [selectedAccount, setSelectedAccount] = useState(null)
  const [showDetailModal, setShowDetailModal] = useState(false)
  const [showAdjustModal, setShowAdjustModal] = useState(false)

  // Adjust balance form
  const [adjustType, setAdjustType] = useState('credit')
  const [adjustAmount, setAdjustAmount] = useState('')
  const [adjustReason, setAdjustReason] = useState('')
  const [adjusting, setAdjusting] = useState(false)

  useEffect(() => {
    console.log('AccountsPage mounted, fetching accounts...')
    fetchAccounts()
  }, [page, search, statusFilter, typeFilter])

  const fetchAccounts = async () => {
    setLoading(true)
    setError(null)

    try {
      const params = { page, limit: 20 }
      if (search) params.search = search
      if (statusFilter) params.status = statusFilter
      if (typeFilter) params.type = typeFilter

      console.log('Fetching accounts with params:', params)
      const res = await AccountsAdminAPI.getAccounts(params)
      console.log('Accounts response:', res)

      if (res.success) {
        setAccounts(res.data.accounts)
        setTotalPages(res.data.pagination.pages)
        setTotal(res.data.pagination.total)
      } else {
        console.error('Failed to load accounts - API error:', res)
        setError(res.message || 'Unknown error from API')
      }
    } catch (err) {
      console.error('Failed to load accounts - Exception:', err)
      setError(`Failed to load accounts: ${err.message}`)
    } finally {
      setLoading(false)
    }
  }

  const handleViewAccount = async (account) => {
    try {
      const res = await AccountsAdminAPI.getAccount(account.id)
      if (res.success) {
        setSelectedAccount(res.data.account)
        setShowDetailModal(true)
      }
    } catch (err) {
      console.error('Failed to load account details')
    }
  }

  const handleAdjustBalance = async () => {
    if (!adjustAmount || !adjustReason) return

    setAdjusting(true)
    try {
      const res = await AccountsAdminAPI.adjustBalance(selectedAccount.id, {
        type: adjustType,
        amount: parseFloat(adjustAmount),
        reason: adjustReason
      })

      if (res.success) {
        setShowAdjustModal(false)
        setAdjustAmount('')
        setAdjustReason('')
        fetchAccounts()
        // Update selected account
        const updatedRes = await AccountsAdminAPI.getAccount(selectedAccount.id)
        if (updatedRes.success) {
          setSelectedAccount(updatedRes.data.account)
        }
      }
    } catch (err) {
      console.error('Failed to adjust balance')
    } finally {
      setAdjusting(false)
    }
  }

  const handleCloseAccount = async (accountId) => {
    if (!confirm('Are you sure you want to close this account?')) return

    try {
      const res = await AccountsAdminAPI.closeAccount(accountId)
      if (res.success) {
        fetchAccounts()
        setShowDetailModal(false)
      }
    } catch (err) {
      console.error('Failed to close account')
    }
  }

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0)
  }

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    })
  }

  const getStatusBadge = (status) => {
    const styles = {
      active: 'bg-emerald-500/20 text-emerald-400',
      frozen: 'bg-blue-500/20 text-blue-400',
      closed: 'bg-red-500/20 text-red-400',
      pending: 'bg-yellow-500/20 text-yellow-400'
    }
    return styles[status] || 'bg-slate-500/20 text-slate-400'
  }

  const getTypeBadge = (type) => {
    const styles = {
      checking: 'bg-blue-500/20 text-blue-400',
      savings: 'bg-emerald-500/20 text-emerald-400',
      investment: 'bg-violet-500/20 text-violet-400',
      crypto: 'bg-orange-500/20 text-orange-400'
    }
    return styles[type] || 'bg-slate-500/20 text-slate-400'
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">Accounts</h1>
          <p className="text-slate-400 mt-1">Manage customer accounts and balances</p>
        </div>
        <div className="text-sm text-slate-400">
          Total: <span className="text-white font-semibold">{total}</span> accounts
        </div>
      </div>

      {/* Filters */}
      <div className="bg-slate-800 border border-slate-700 rounded-xl p-4">
        <div className="flex flex-col md:flex-row gap-4">
          {/* Search */}
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
            <input
              type="text"
              placeholder="Search by account number, name, email..."
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1) }}
              className="w-full pl-10 pr-4 py-2.5 bg-slate-900/50 border border-slate-600 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:border-blue-500"
            />
          </div>

          {/* Status Filter */}
          <select
            value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value); setPage(1) }}
            className="px-4 py-2.5 bg-slate-900/50 border border-slate-600 rounded-lg text-white focus:outline-none focus:border-blue-500"
          >
            <option value="">All Status</option>
            <option value="active">Active</option>
            <option value="frozen">Frozen</option>
            <option value="closed">Closed</option>
          </select>

          {/* Type Filter */}
          <select
            value={typeFilter}
            onChange={(e) => { setTypeFilter(e.target.value); setPage(1) }}
            className="px-4 py-2.5 bg-slate-900/50 border border-slate-600 rounded-lg text-white focus:outline-none focus:border-blue-500"
          >
            <option value="">All Types</option>
            <option value="checking">Checking</option>
            <option value="savings">Savings</option>
            <option value="investment">Investment</option>
            <option value="crypto">Crypto</option>
          </select>

          <button
            onClick={fetchAccounts}
            className="px-4 py-2.5 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors flex items-center gap-2"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="bg-slate-800 border border-slate-700 rounded-xl overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
          </div>
        ) : error ? (
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <AlertCircle className="w-10 h-10 text-red-500 mx-auto mb-2" />
              <p className="text-slate-400">{error}</p>
            </div>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-700 bg-slate-900/50">
                    <th className="text-left py-4 px-4 text-sm font-medium text-slate-400">Account</th>
                    <th className="text-left py-4 px-4 text-sm font-medium text-slate-400">Customer</th>
                    <th className="text-left py-4 px-4 text-sm font-medium text-slate-400">Type</th>
                    <th className="text-right py-4 px-4 text-sm font-medium text-slate-400">Balance</th>
                    <th className="text-left py-4 px-4 text-sm font-medium text-slate-400">Status</th>
                    <th className="text-left py-4 px-4 text-sm font-medium text-slate-400">Opened</th>
                    <th className="text-center py-4 px-4 text-sm font-medium text-slate-400">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {accounts.map((account) => (
                    <tr key={account.id} className="border-b border-slate-700/50 hover:bg-slate-700/30 transition-colors">
                      <td className="py-4 px-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-slate-700 rounded-lg flex items-center justify-center">
                            <CreditCard className="w-5 h-5 text-slate-400" />
                          </div>
                          <div>
                            <p className="text-white font-medium font-mono">{account.account_number}</p>
                            <p className="text-slate-500 text-xs">{account.name || 'Primary Account'}</p>
                          </div>
                        </div>
                      </td>
                      <td className="py-4 px-4">
                        <p className="text-white">{account.first_name} {account.last_name}</p>
                        <p className="text-slate-500 text-sm">{account.email}</p>
                      </td>
                      <td className="py-4 px-4">
                        <span className={`px-2 py-1 rounded text-xs font-medium capitalize ${getTypeBadge(account.account_type)}`}>
                          {account.account_type}
                        </span>
                      </td>
                      <td className="py-4 px-4 text-right">
                        <span className="text-white font-semibold">{formatCurrency(account.balance)}</span>
                      </td>
                      <td className="py-4 px-4">
                        <span className={`px-2 py-1 rounded text-xs font-medium capitalize ${getStatusBadge(account.status)}`}>
                          {account.status}
                        </span>
                      </td>
                      <td className="py-4 px-4 text-slate-400 text-sm">
                        {formatDate(account.created_at)}
                      </td>
                      <td className="py-4 px-4">
                        <div className="flex items-center justify-center gap-2">
                          <button
                            onClick={() => handleViewAccount(account)}
                            className="p-2 hover:bg-slate-600 rounded-lg transition-colors"
                            title="View Details"
                          >
                            <Eye className="w-4 h-4 text-slate-400" />
                          </button>
                          {hasPermission('accounts.edit') && account.status === 'active' && (
                            <button
                              onClick={() => {
                                setSelectedAccount(account)
                                setShowAdjustModal(true)
                              }}
                              className="p-2 hover:bg-slate-600 rounded-lg transition-colors"
                              title="Adjust Balance"
                            >
                              <DollarSign className="w-4 h-4 text-emerald-400" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between px-4 py-3 border-t border-slate-700">
                <p className="text-sm text-slate-400">
                  Page {page} of {totalPages}
                </p>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="p-2 hover:bg-slate-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronLeft className="w-5 h-5 text-slate-400" />
                  </button>
                  <button
                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                    className="p-2 hover:bg-slate-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronRight className="w-5 h-5 text-slate-400" />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Account Detail Modal */}
      <AnimatePresence>
        {showDetailModal && selectedAccount && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setShowDetailModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-slate-800 border border-slate-700 rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
              onClick={e => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div className="p-6 border-b border-slate-700 flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center">
                    <CreditCard className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-white">Account Details</h2>
                    <p className="text-slate-400 font-mono">{selectedAccount.account_number}</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowDetailModal(false)}
                  className="p-2 hover:bg-slate-700 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5 text-slate-400" />
                </button>
              </div>

              {/* Modal Body */}
              <div className="p-6 space-y-6">
                {/* Balance Card */}
                <div className="bg-gradient-to-br from-blue-500/20 to-indigo-500/20 border border-blue-500/30 rounded-xl p-6">
                  <p className="text-slate-400 text-sm mb-2">Current Balance</p>
                  <p className="text-4xl font-bold text-white">{formatCurrency(selectedAccount.balance)}</p>
                  <div className="mt-4 flex items-center gap-4">
                    <span className={`px-3 py-1 rounded-lg text-sm font-medium capitalize ${getStatusBadge(selectedAccount.status)}`}>
                      {selectedAccount.status}
                    </span>
                    <span className={`px-3 py-1 rounded-lg text-sm font-medium capitalize ${getTypeBadge(selectedAccount.account_type)}`}>
                      {selectedAccount.account_type}
                    </span>
                  </div>
                </div>

                {/* Account Info */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-slate-900/50 rounded-lg p-4">
                    <div className="flex items-center gap-2 text-slate-400 text-sm mb-1">
                      <User className="w-4 h-4" />
                      Account Holder
                    </div>
                    <p className="text-white">{selectedAccount.first_name} {selectedAccount.last_name}</p>
                  </div>
                  <div className="bg-slate-900/50 rounded-lg p-4">
                    <div className="flex items-center gap-2 text-slate-400 text-sm mb-1">
                      <Calendar className="w-4 h-4" />
                      Opened Date
                    </div>
                    <p className="text-white">{formatDate(selectedAccount.created_at)}</p>
                  </div>
                  <div className="bg-slate-900/50 rounded-lg p-4">
                    <div className="flex items-center gap-2 text-slate-400 text-sm mb-1">
                      <Hash className="w-4 h-4" />
                      Account ID
                    </div>
                    <p className="text-white font-mono text-sm">{selectedAccount.id}</p>
                  </div>
                  <div className="bg-slate-900/50 rounded-lg p-4">
                    <div className="flex items-center gap-2 text-slate-400 text-sm mb-1">
                      <TrendingUp className="w-4 h-4" />
                      Interest Rate
                    </div>
                    <p className="text-white">{selectedAccount.interest_rate || '0.00'}%</p>
                  </div>
                </div>

                {/* Recent Transactions */}
                {selectedAccount.recentTransactions?.length > 0 && (
                  <div>
                    <h3 className="text-lg font-semibold text-white mb-3">Recent Transactions</h3>
                    <div className="space-y-2">
                      {selectedAccount.recentTransactions.map((tx, i) => (
                        <div key={i} className="flex items-center justify-between p-3 bg-slate-900/50 rounded-lg">
                          <div className="flex items-center gap-3">
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                              tx.type === 'deposit' ? 'bg-emerald-500/20' : 'bg-red-500/20'
                            }`}>
                              {tx.type === 'deposit' ? (
                                <TrendingUp className="w-4 h-4 text-emerald-400" />
                              ) : (
                                <TrendingDown className="w-4 h-4 text-red-400" />
                              )}
                            </div>
                            <div>
                              <p className="text-white text-sm">{tx.description}</p>
                              <p className="text-slate-500 text-xs">{formatDate(tx.created_at)}</p>
                            </div>
                          </div>
                          <span className={`font-semibold ${
                            tx.type === 'deposit' ? 'text-emerald-400' : 'text-red-400'
                          }`}>
                            {tx.type === 'deposit' ? '+' : '-'}{formatCurrency(Math.abs(tx.amount))}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Actions */}
                {hasPermission('accounts.edit') && selectedAccount.status === 'active' && (
                  <div className="flex gap-3 pt-4 border-t border-slate-700">
                    <button
                      onClick={() => {
                        setShowDetailModal(false)
                        setShowAdjustModal(true)
                      }}
                      className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg transition-colors"
                    >
                      <DollarSign className="w-5 h-5" />
                      Adjust Balance
                    </button>
                    <button
                      onClick={() => handleCloseAccount(selectedAccount.id)}
                      className="flex items-center justify-center gap-2 px-4 py-3 bg-red-500/20 hover:bg-red-500/30 text-red-400 rounded-lg transition-colors"
                    >
                      <Ban className="w-5 h-5" />
                      Close Account
                    </button>
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Adjust Balance Modal */}
      <AnimatePresence>
        {showAdjustModal && selectedAccount && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setShowAdjustModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-slate-800 border border-slate-700 rounded-2xl w-full max-w-md"
              onClick={e => e.stopPropagation()}
            >
              <div className="p-6 border-b border-slate-700">
                <h2 className="text-xl font-bold text-white">Adjust Balance</h2>
                <p className="text-slate-400 text-sm mt-1">Account: {selectedAccount.account_number}</p>
              </div>

              <div className="p-6 space-y-4">
                <div className="text-center p-4 bg-slate-900/50 rounded-lg">
                  <p className="text-slate-400 text-sm">Current Balance</p>
                  <p className="text-2xl font-bold text-white">{formatCurrency(selectedAccount.balance)}</p>
                </div>

                {/* Adjustment Type */}
                <div className="flex gap-2">
                  <button
                    onClick={() => setAdjustType('credit')}
                    className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg transition-colors ${
                      adjustType === 'credit'
                        ? 'bg-emerald-500 text-white'
                        : 'bg-slate-700 text-slate-400 hover:bg-slate-600'
                    }`}
                  >
                    <Plus className="w-5 h-5" />
                    Credit
                  </button>
                  <button
                    onClick={() => setAdjustType('debit')}
                    className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg transition-colors ${
                      adjustType === 'debit'
                        ? 'bg-red-500 text-white'
                        : 'bg-slate-700 text-slate-400 hover:bg-slate-600'
                    }`}
                  >
                    <Minus className="w-5 h-5" />
                    Debit
                  </button>
                </div>

                {/* Amount */}
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Amount
                  </label>
                  <div className="relative">
                    <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                    <input
                      type="number"
                      value={adjustAmount}
                      onChange={(e) => setAdjustAmount(e.target.value)}
                      placeholder="0.00"
                      step="0.01"
                      min="0"
                      className="w-full pl-10 pr-4 py-3 bg-slate-900/50 border border-slate-600 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:border-blue-500"
                    />
                  </div>
                </div>

                {/* Reason */}
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Reason
                  </label>
                  <textarea
                    value={adjustReason}
                    onChange={(e) => setAdjustReason(e.target.value)}
                    placeholder="Provide a reason for this adjustment..."
                    rows={3}
                    className="w-full px-4 py-3 bg-slate-900/50 border border-slate-600 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:border-blue-500 resize-none"
                  />
                </div>

                {/* Preview */}
                {adjustAmount && (
                  <div className="p-4 bg-slate-900/50 rounded-lg border border-slate-700">
                    <p className="text-slate-400 text-sm">New Balance</p>
                    <p className={`text-2xl font-bold ${adjustType === 'credit' ? 'text-emerald-400' : 'text-red-400'}`}>
                      {formatCurrency(
                        adjustType === 'credit'
                          ? (selectedAccount.balance || 0) + parseFloat(adjustAmount || 0)
                          : (selectedAccount.balance || 0) - parseFloat(adjustAmount || 0)
                      )}
                    </p>
                  </div>
                )}
              </div>

              <div className="p-6 border-t border-slate-700 flex gap-3">
                <button
                  onClick={() => setShowAdjustModal(false)}
                  className="flex-1 px-4 py-3 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleAdjustBalance}
                  disabled={!adjustAmount || !adjustReason || adjusting}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {adjusting ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="w-5 h-5" />
                      Confirm Adjustment
                    </>
                  )}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}
